import React, { useState, useEffect, useCallback } from "react";
import classNames from "classnames";
import { chevronDown, documentTextOutline } from "ionicons/icons";
import { IonIcon } from "@ionic/react";
import { FirebaseAnalytics } from "@ionic-native/firebase-analytics";
import PageLayout from "../layouts/PageLayout";
import { ListRow, Toggle } from "../components";
import {
  AdapterListItem,
  UIServiceState,
  VPNProvider,
  ClientInfoField,
} from "../model";
import { createPollAndRefresh } from "../utils";
import api from "../api";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch, RootState } from "store";
import { useTranslation } from "react-i18next";
import Title from "components/Title";
import Input, { PasswordInput } from "components/Input";
import RadioSelector from "components/RadioSelector";
import vest, { enforce, test } from "vest";
import Button from "components/Button";
import { useHistory } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import Select from "react-select";
import { FadeLoader } from "react-spinners";

///////////////////////////////////////////////////////////////
//   MAIN COMPONENT
///////////////////////////////////////////////////////////////
interface SettingsProps {
  adapterId: string;
  pheroVpn: boolean;
}

const Settings: React.FC<SettingsProps> = ({ adapterId, pheroVpn }) => {
  const history = useHistory();
  const dispatch = useDispatch<Dispatch>();
  const { t } = useTranslation("securitySettings");

  const { adapterDetails } = useSelector((state: RootState) => ({
    adapterDetails: state.adapter.adapter!,
  }));

  const providerData = useSelector(
    (state: RootState) => state.vpn.adapterVPNProviderMap
  );

  type ConfigServices = "adblocking" | "malware" | "vpn" | "vpn-killswitch";
  type ServiceStatus = {
    [key in ConfigServices]: UIServiceState | undefined;
  };
  const [serviceStatus, setServiceStatus] = useState<ServiceStatus>({
    adblocking: undefined,
    malware: undefined,
    vpn: undefined,
    "vpn-killswitch": undefined,
  });
  const [providerSelectionVisible, setProviderSelectionVisible] =
    useState<boolean>(false);
  const [providerGroups, setProviderGroups] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [groupRadioOptions, setGroupRadioOptions] = useState<any[]>([]);
  const [selectedVPNProvider, setSelectedVPNProvider] = useState<VPNProvider>();
  const [currentFieldValues, setCurrentFieldValues] = useState<{
    [field: string]: string;
  }>({});
  const [fieldErrorMessages, setFieldErrorMessages] = useState<{
    [field: string]: string;
  }>({});
  const [optionalSectionVisible, setOptionalSectionVisible] =
    useState<boolean>(false);
  const [optionalSectionExpanded, setOptionalSectionExpanded] =
    useState<boolean>(false);
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);
  const [dropzoneEnabled, setDropzoneEnabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resetVPNVisible, setResetVPNVisible] = useState<boolean>(false);

  const validateField = vest.create((data: any) => {
    test(data.field.field, t("validationError"), () => {
      enforce(data.value).matches(data.field.options.validation);
    });
  });

  const currentProvider = adapterDetails.settings.vpn?.[0]?.provider;

  useEffect(() => {
    FirebaseAnalytics.logEvent("init_page", { page: "security_settings" });

    Promise.resolve(dispatch.vpn.getVPNProviders({}))
      .then((vpn_providers) => {
        let groupNames: string[] = [];
        vpn_providers.forEach((provider) => {
          if (
            //isProviderSupported(provider) &&
            groupNames.indexOf(provider.group_name) === -1
          ) {
            groupNames.push(provider.group_name);
          }
        });
        setProviderGroups(groupNames);
      })
      .catch((error) => {
        console.log("Error getting VPN providers: ", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateStateFromAdapter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapterDetails.id, currentProvider, providerGroups]);

  useEffect(() => {
    let options: any[] = [];
    providerData.forEach((provider) => {
      if (
        //isProviderSupported(provider) &&
        provider.group_name === selectedGroup
      ) {
        options.push({
          label: provider.display_name,
          value: provider.provider,
        });
      }
    });
    setGroupRadioOptions(options);

    if (
      adapterDetails.settings.vpn !== undefined &&
      adapterDetails.settings.vpn.length > 0
    ) {
      const vpnProvider = providerData.find(
        (vpnProvider) =>
          vpnProvider.provider === adapterDetails.settings.vpn![0].provider
      );
      if (
        vpnProvider !== undefined &&
        vpnProvider.group_name === selectedGroup
      ) {
        setSelectedVPNProvider(vpnProvider);
      } else {
        const vpnProvider = providerData.find(
          (vpnProvider) =>
            //isProviderSupported(vpnProvider) &&
            vpnProvider.group_name === selectedGroup
        );
        setSelectedVPNProvider(vpnProvider);
      }
    } else {
      const vpnProvider = providerData.find(
        (vpnProvider) =>
          //isProviderSupported(vpnProvider) &&
          vpnProvider.group_name === selectedGroup
      );
      setSelectedVPNProvider(vpnProvider);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  useEffect(() => {
    if (selectedVPNProvider !== undefined) {
      setOptionalSectionVisible(
        selectedVPNProvider.creds.filter(
          (field) => field.optional && !field.options.hidden
        ).length > 0
      );

      setSelectedGroup(selectedVPNProvider.group_name);

      const credentials =
        adapterDetails.credentials[selectedVPNProvider.provider];
      let newFieldValues: { [field: string]: string } = {};
      let newOptionalSectionExpanded = false;
      selectedVPNProvider.creds.forEach((field) => {
        if (
          credentials !== undefined &&
          field.field in credentials &&
          credentials[field.field as keyof typeof credentials]
        ) {
          newFieldValues[field.field] = credentials[
            field.field as keyof typeof credentials
          ] as string;

          if (field.optional) {
            newOptionalSectionExpanded = true;
          }
        } else {
          newFieldValues[field.field] =
            field.options.selections.length > 0
              ? field.field === "country"
                ? adapterDetails?.country || ""
                : field.options.selections[0]
              : "";
        }
      });
      setCurrentFieldValues(newFieldValues);
      setOptionalSectionExpanded(newOptionalSectionExpanded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVPNProvider]);

  useEffect(() => {
    if (selectedVPNProvider !== undefined) {
      let fieldErrorMessages: { [field: string]: string } = {};
      let allFieldsReady = true;

      selectedVPNProvider.creds.forEach((field) => {
        if (currentFieldValues[field.field] !== "") {
          if (field.options.validation) {
            const validationState = validateField({
              field: field,
              value: currentFieldValues[field.field],
            });

            fieldErrorMessages[field.field] =
              validationState.getErrors(field.field).shift() || "";

            if (validationState.hasErrors()) {
              allFieldsReady = false;
            }
          } else {
            fieldErrorMessages[field.field] = "";
          }
        } else {
          fieldErrorMessages[field.field] = "";

          if (!field.optional) {
            allFieldsReady = false;
          }
        }
      });
      setFieldErrorMessages(fieldErrorMessages);
      setSubmitEnabled(allFieldsReady);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFieldValues]);

  useEffect(() => {
    if (!optionalSectionExpanded && selectedVPNProvider !== undefined) {
      let newFieldValues = {
        ...currentFieldValues,
      };
      selectedVPNProvider.creds.forEach((field) => {
        if (field.optional) {
          newFieldValues[field.field] =
            field.options.selections.length > 0
              ? field.options.selections[0]
              : "";
        }
      });
      setCurrentFieldValues(newFieldValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionalSectionExpanded]);

  const isProviderSupported = (provider: VPNProvider) => {
    if (adapterDetails.fw_status?.fw.os) {
      const appVersion = adapterDetails.fw_status.fw.os.version
        .split("-")[0]
        .substring(1);
      return (
        appVersion.localeCompare(provider.supported_version, undefined, {
          numeric: true,
        }) >= 0
      );
    } else {
      // local environment will not support FDS, display all providers for testing purposes
      if (process.env.REACT_APP_ENV === "dev") {
        return true;
      }
    }
    return false;
  };

  const updateStateFromAdapter = () => {
    if (
      adapterDetails.settings.vpn !== undefined &&
      adapterDetails.settings.vpn.length > 0
    ) {
      const vpnProvider = providerData.find(
        (vpnProvider) =>
          vpnProvider.provider === adapterDetails.settings.vpn![0].provider
      );
      if (vpnProvider !== undefined) {
        setSelectedVPNProvider(vpnProvider);
      } else {
        setSelectedGroup(providerGroups[0]);
      }
    } else {
      setSelectedGroup(providerGroups[0]);
    }
  };

  const onSave = () => {
    if (selectedVPNProvider !== undefined) {
      setProviderSelectionVisible(false);
      let vpnConfig = {
        id: adapterId,
        provider: selectedVPNProvider.provider,
        country: undefined,
        creds: currentFieldValues,
      };
      let displayName =
        selectedVPNProvider.display_name === selectedVPNProvider.group_name
          ? selectedVPNProvider.display_name
          : selectedVPNProvider.group_name +
            " (" +
            selectedVPNProvider.display_name +
            ")";
      dispatch.vpn
        .configureVPNProvider({
          config: vpnConfig,
          updateUi: (state) => {
            setServiceStatus({ ...serviceStatus, vpn: state });

            if (state === "Failed!") {
              updateStateFromAdapter();
            }
          },
          display_name: displayName,
        })
        .catch(() => {
          setServiceStatus({ ...serviceStatus, vpn: "Failed!" });
        });
    }
  };

  const onCancel = () => {
    setProviderSelectionVisible(false);
    setResetVPNVisible(false);
    updateStateFromAdapter();
  };

  const onFieldChange = (field: string, value: string) => {
    setCurrentFieldValues({ ...currentFieldValues, [field]: value });
  };

  const onServiceToggled = (service: ConfigServices, state: boolean) => {
    setServiceStatus({ ...serviceStatus, [service]: "Updating" });
    api.adapter
      .changeConfigurationService({
        id: adapterId,
        service,
        state,
      })
      .then(() =>
        createPollAndRefresh<AdapterListItem | undefined>(
          () => dispatch.adapter.getAdapter(adapterId),
          (adapter?) => adapter?.services[service]?.state === state,
          (state) => setServiceStatus({ ...serviceStatus, [service]: state }),
          10,
          1500
        )
      )
      .catch(() =>
        setServiceStatus({ ...serviceStatus, [service]: "Failed!" })
      );
  };

  const onResetVPNSettings = () => {
    setResetVPNVisible(false);
    api.vpn.resetVPN({ id: adapterId });
    //updateStateFromAdapter();
  };

  return (
    <PageLayout testId="security-settings">
      <div className="module-large">
        <Title title={t("title")} />
        <ul className="data-list">
          <ListRow
            key="vpn_selection"
            text={t("vpn")}
            status={serviceStatus.vpn}
            disabled={!adapterDetails.services.online.state}
            className="self-center flex flex-col justify-between w-full"
          >
            <div className="flex flex-col justify-between w-full">
              <div className="flex flex-row justify-end w-full relative">
                {/* VPN Selection Goes Here */}
                {providerSelectionVisible && (
                  <div
                    id="provider_selection"
                    className="flex flex-col justify-around items-start x-40 bg-gray-100 rounded-xl shadow-2xl drop-shadow-lg absolute z-20 px-5 py-4 position-abs-center-horizontally"
                  >
                    <h2 className="mt-2 mb-5 text-left text-xl font-semibold whitespace-nowrap">
                      Change VPN service provider
                    </h2>

                    <div className="flex flex-row justify-start items-center w-full gap-3">
                      <label
                        htmlFor="Provider Selection"
                        className="text-md text-center font-medium my-3 whitespace-nowrap bold text-gray-500"
                      >
                        {t("vpnProviderSelect")}
                      </label>
                      <div className="select-container w-full">
                        <select
                          id="provider"
                          data-testid="provider-select"
                          value={selectedGroup}
                          className="w-full mt-1 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-orange focus:border-orange sm:text-sm rounded-md"
                          onChange={(e) => {
                            setSelectedGroup(e.currentTarget.value);
                          }}
                        >
                          {providerGroups.map((groupName) => (
                            <option
                              data-testid={`vpngroup-${groupName}`}
                              key={groupName}
                              value={groupName}
                            >
                              {groupName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {groupRadioOptions.length > 1 && (
                      <div
                        className="flex flex-row"
                        style={{ paddingTop: "10px" }}
                      >
                        <RadioSelector
                          onSelect={(value) => {
                            const vpnProvider = providerData.find(
                              (vpnProvider) => vpnProvider.provider === value
                            );
                            setSelectedVPNProvider(vpnProvider);
                          }}
                          options={groupRadioOptions}
                          value={selectedVPNProvider?.provider!}
                          title={""}
                        />
                      </div>
                    )}

                    <div className="flex flex-col justify-start items-center w-full mt-5 gap-3">
                      {selectedVPNProvider?.creds.map(
                        (credsData) =>
                          !credsData.optional && (
                            <CredsField
                              credsData={credsData}
                              fieldValue={currentFieldValues[credsData.field]}
                              fieldErrorMessage={
                                fieldErrorMessages[credsData.field]
                              }
                              onChange={onFieldChange}
                              dropzoneEnabled={dropzoneEnabled}
                              setDropzoneEnabled={setDropzoneEnabled}
                              selectedVPNProvider={selectedVPNProvider}
                              country={
                                adapterDetails.credentials[
                                  selectedVPNProvider.provider
                                ]?.country
                                  ? adapterDetails.credentials[
                                      selectedVPNProvider.provider
                                    ].country
                                  : adapterDetails?.country
                              }
                              currentFieldValues={currentFieldValues}
                            />
                          )
                      )}
                    </div>

                    {optionalSectionVisible && (
                      <>
                        <div
                          className="flex flex-row justify-between items-center w-full mt-5 select-none cursor-pointer"
                          onClick={() => {
                            setOptionalSectionExpanded(
                              !optionalSectionExpanded
                            );
                          }}
                        >
                          <h4 className="m-0 p-0">
                            Advanced options:
                            <span
                              className={classNames("ml-1 font-semibold", {
                                "is-active text-rose-500":
                                  !optionalSectionExpanded,
                                "text-green-600": optionalSectionExpanded,
                              })}
                            >
                              {optionalSectionExpanded ? "On" : "Off"}
                            </span>
                          </h4>
                          <IonIcon
                            icon={chevronDown}
                            className={classNames(
                              optionalSectionExpanded
                                ? "-rotate-180"
                                : "rotate-0",
                              "h-6 w-6 transform"
                            )}
                            aria-hidden="true"
                          />
                        </div>

                        {optionalSectionExpanded && (
                          <div className="flex flex-col justify-start items-center w-full mt-2 gap-3">
                            {selectedVPNProvider?.creds.map(
                              (credsData) =>
                                credsData.optional && (
                                  <CredsField
                                    credsData={credsData}
                                    fieldValue={
                                      currentFieldValues[credsData.field]
                                    }
                                    fieldErrorMessage={
                                      fieldErrorMessages[credsData.field]
                                    }
                                    onChange={onFieldChange}
                                    dropzoneEnabled={dropzoneEnabled}
                                    setDropzoneEnabled={setDropzoneEnabled}
                                    selectedVPNProvider={selectedVPNProvider}
                                    currentFieldValues={currentFieldValues}
                                  />
                                )
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {!pheroVpn && selectedVPNProvider?.provider === "phero" && (
                      <div className="flex flex-row justify-center items-center mt-5">
                        <p style={{ whiteSpace: "pre-line", width: "80%" }}>
                          {t("pheroVpnMessage")}
                        </p>
                      </div>
                    )}

                    <div className="flex flex-row justify-end items-center w-full mt-5">
                      {(pheroVpn ||
                        selectedVPNProvider?.provider !== "phero") &&
                        (selectedVPNProvider &&
                        isProviderSupported(selectedVPNProvider) ? (
                          <Button
                            data-testid="submit"
                            className="text-center mr-3"
                            size="small"
                            type="button"
                            disabled={!submitEnabled}
                            onClick={onSave}
                          >
                            Save
                          </Button>
                        ) : (
                          <Button
                            data-testid="submit"
                            className="text-center mr-3"
                            size="small"
                            type="button"
                            disabled={true}
                          >
                            Not Supported
                          </Button>
                        ))}
                      {!pheroVpn &&
                        selectedVPNProvider?.provider === "phero" && (
                          <Button
                            data-testid="submit"
                            className="text-center mr-3"
                            size="small"
                            type="button"
                            disabled={false}
                            onClick={() =>
                              history.push(
                                `/account/upgrade/${adapterDetails.id}`
                              )
                            }
                          >
                            Upgrade
                          </Button>
                        )}
                      <Button
                        data-testid="back_select_provider"
                        className="text-center"
                        size="small"
                        type="button"
                        variant="white"
                        disabled={false}
                        onClick={onCancel}
                      >
                        Cancel
                      </Button>
                    </div>
                    {isLoading && (
                      <div
                        style={{
                          backgroundColor: "rgba(0,0,0,0.5)",
                          position: "absolute",
                          top: 0,
                          right: 0,
                          left: 0,
                          bottom: 0,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <FadeLoader color="#f97516" />
                        <span className="text-white text-sm">Loading...</span>
                      </div>
                    )}
                  </div>
                )}
                {/* Reset VPN Goes Here */}
                {resetVPNVisible && (
                  <div
                    id="provider_selection"
                    className="flex flex-col justify-around items-start x-40 bg-gray-100 rounded-xl shadow-2xl drop-shadow-lg absolute z-20 px-5 py-4 position-abs-center-horizontally"
                  >
                    <h2 className="mt-2 mb-5 text-left text-xl font-semibold whitespace-nowrap">
                      Reset VPN
                    </h2>

                    <div className="flex flex-row justify-start items-center w-full gap-3">
                      <label
                        htmlFor="Provider Selection"
                        className="text-md text-center font-medium my-3 whitespace-nowrap bold text-gray-500"
                      >
                        {t("resetVPNBody1")}
                        <br />
                        <br />
                        {t("resetVPNBody2")}
                      </label>
                    </div>
                    <div className="flex flex-row justify-end items-center w-full mt-5">
                      <Button
                        data-testid="cancel_reset_vpn_settings"
                        className="text-center mr-3"
                        size="small"
                        type="button"
                        variant="white"
                        disabled={false}
                        onClick={onCancel}
                      >
                        Cancel
                      </Button>
                      <Button
                        data-testid="reset_vpn_settings"
                        className="text-center"
                        size="small"
                        type="button"
                        disabled={false}
                        onClick={onResetVPNSettings}
                      >
                        Reset VPN
                      </Button>
                    </div>
                  </div>
                )}
                <div
                  id="provider_status"
                  className="flex flex-col items-center grow flex-1 justify-center gap-3"
                >
                  <div className="flex flex-row items-center justify-center">
                    <label
                      htmlFor="Provider Selection Name"
                      className="text-md mx-5 py-3 text-center font-medium bold text-gray-500"
                    >
                      {selectedVPNProvider?.group_name}
                    </label>
                    <Button
                      data-testid="select_provider"
                      className="text-center"
                      type="button"
                      size="small"
                      disabled={!adapterDetails.services.online.state}
                      onClick={() => setProviderSelectionVisible(true)}
                    >
                      Change
                    </Button>
                    {adapterDetails.status?.vpn === 2 /*BadgeColor.RED*/ && (
                      <Button
                        data-testid="open_reset_vpn_settings"
                        className="mx-5 text-center"
                        type="button"
                        size="small"
                        disabled={false}
                        onClick={() => setResetVPNVisible(true)}
                      >
                        Reset VPN
                      </Button>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="Connected To"
                      className="text-md mx-5 py-3 text-center font-medium bold text-gray-500"
                    >
                      {adapterDetails.services?.vpn?.state === true &&
                      adapterDetails.status?.vpn !== 2 &&
                      adapterDetails.status?.vpn_connections?.[
                        adapterDetails.status.active_server!
                      ]?.provider === selectedVPNProvider?.provider &&
                      adapterDetails.status?.vpn_connections?.[
                        adapterDetails.status.active_server!
                      ]?.up === true
                        ? "Connected to " +
                          adapterDetails.status?.vpn_connections?.[
                            adapterDetails.status.active_server!
                          ]?.connected_to +
                          (selectedVPNProvider?.display_name ===
                          selectedVPNProvider?.group_name
                            ? ""
                            : " (" + selectedVPNProvider?.display_name + ")")
                        : "Not connected"}
                      {adapterDetails.status?.vpn_connections?.[
                        adapterDetails.status.active_server!
                      ]?.error !== undefined &&
                      adapterDetails.status?.vpn_connections?.[
                        adapterDetails.status.active_server!
                      ]?.error !== null &&
                      adapterDetails.status?.vpn_connections?.[
                        adapterDetails.status.active_server!
                      ]?.error !== ""
                        ? "(" +
                          adapterDetails.status?.vpn_connections?.[
                            adapterDetails.status.active_server!
                          ]?.error +
                          ")"
                        : null}
                    </label>
                  </div>
                </div>
                <div className="self-center">
                  <Toggle
                    onChange={(state) => onServiceToggled("vpn", state)}
                    checked={adapterDetails.services.vpn.state}
                    id="vpn-active"
                    disabled={!adapterDetails.services.online.state}
                  />
                </div>
              </div>
            </div>
          </ListRow>
          <ListRow
            text={t("vpn_killswitch")}
            status={serviceStatus["vpn-killswitch"]}
            disabled={!adapterDetails.services.online.state}
          >
            <Toggle
              onChange={(state) => onServiceToggled("vpn-killswitch", state)}
              checked={adapterDetails.services["vpn-killswitch"].state}
              id="vpn-killswitch"
              disabled={!adapterDetails.services.online.state}
            />
          </ListRow>
          <ListRow
            key="phishing"
            text={t("malware")}
            status={serviceStatus.malware}
            disabled={!adapterDetails.services.online.state}
          >
            <Toggle
              onChange={(state) => onServiceToggled("malware", state)}
              checked={adapterDetails.services.malware.state}
              id="malware-phishing"
              disabled={!adapterDetails.services.online.state}
            />
          </ListRow>
          <ListRow
            text={t("adBlocking")}
            status={serviceStatus.adblocking}
            disabled={!adapterDetails.services.online.state}
          >
            <Toggle
              onChange={(state) => onServiceToggled("adblocking", state)}
              checked={adapterDetails.services.adblocking.state}
              id="adblocking"
              disabled={!adapterDetails.services.online.state}
            />
          </ListRow>
        </ul>
      </div>
    </PageLayout>
  );
};

export default Settings;

///////////////////////////////////////////////////////////////
//   CREDS FIELD COMPONENT
///////////////////////////////////////////////////////////////
interface CredsFieldProps {
  credsData: ClientInfoField;
  fieldValue: string;
  fieldErrorMessage: string;
  onChange: any;
  dropzoneEnabled: boolean;
  setDropzoneEnabled: any;
  selectedVPNProvider: VPNProvider;
  country?: string | null;
  currentFieldValues: {
    [field: string]: string;
  };
}

const CredsField: React.FC<CredsFieldProps> = ({
  credsData,
  fieldValue,
  fieldErrorMessage,
  onChange,
  dropzoneEnabled,
  setDropzoneEnabled,
  selectedVPNProvider,
  country,
  currentFieldValues,
}) => {
  const { t } = useTranslation("securitySettings");

  const getCleanOptions = (options: string[]) => {
    let newOriginalOptions = [...options];
    let newOptions = [];
    newOriginalOptions.sort((a, b) => {
      const aSplit = a.split("-");
      const bSplit = b.split("-");
      return aSplit[1].localeCompare(bSplit[1]);
    });
    for (const option of newOriginalOptions) {
      let newOption = {
        value: option.split("-")[0],
        label: option.split("-")[1],
      };
      if (option.split("-")[0] === country) {
        newOptions.unshift(newOption);
      } else {
        newOptions.push(newOption);
      }
    }
    return newOptions;
  };

  useEffect(() => {
    if (credsData.field === "country") {
      onChange(credsData.field, country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  return (
    <>
      {!credsData.options.hidden && (
        <div className="basis-full w-full">
          {!credsData.options.password &&
            credsData.options.selections.length === 0 &&
            (!dropzoneEnabled || !credsData.options.multiline) && (
              <Input
                id={credsData.field}
                data-testid={credsData.field}
                label={credsData.prompt}
                inputProps={{
                  value: fieldValue,
                  onChange: (e: any) => {
                    onChange(credsData.field, e.target.value);
                  },
                }}
                error={fieldErrorMessage}
                type="text"
                placeholder=""
                useTextArea={credsData.options.multiline}
              />
            )}
          {!credsData.options.password &&
            credsData.options.selections.length === 0 &&
            credsData.options.multiline &&
            dropzoneEnabled && (
              <DropZone
                label={credsData.prompt}
                id={credsData.field}
                inputProps={{
                  value: fieldValue,
                  onChange: (value: any) => {
                    onChange(credsData.field, value);
                  },
                }}
                error={fieldErrorMessage}
                acceptedTypes={["txt"]}
                setDropzoneOff={() => setDropzoneEnabled(false)}
                selectedVPNProvider={selectedVPNProvider}
              />
            )}
          {!credsData.options.password &&
            credsData.options.selections.length === 0 &&
            credsData.options.multiline &&
            (dropzoneEnabled ? (
              <div style={{ fontSize: "12px", padding: "5px" }}>
                <b
                  style={{ color: "#f97516" }}
                  className="cursor-pointer"
                  onClick={() => setDropzoneEnabled(false)}
                >
                  Click Here
                </b>
                {t("enterManually")}
              </div>
            ) : (
              <div style={{ fontSize: "12px", padding: "5px" }}>
                <b
                  style={{ color: "#f97516" }}
                  className="cursor-pointer"
                  onClick={() => setDropzoneEnabled(true)}
                >
                  Click Here
                </b>
                {t("uploadFile")}
              </div>
            ))}
          {credsData.options.password && (
            <PasswordInput
              inputTagClassname="pr-8"
              id={credsData.field}
              data-testid={credsData.field}
              label={credsData.prompt}
              inputProps={{
                autoComplete: "new-password",
                value: fieldValue,
                onChange: (e: any) => {
                  onChange(credsData.field, e.target.value);
                },
              }}
              error={fieldErrorMessage}
              placeholder=""
            />
          )}
          {credsData.field !== "country" &&
            credsData.options.selections.length > 0 && (
              <div className="flex flex-col">
                <label className="pb-1 block text-sm whitespace-nowrap font-medium text-gray-700">
                  {credsData.prompt}
                </label>
                <select
                  id={credsData.field}
                  data-testid={credsData.field}
                  className="block w-full pl-3 pr-10 px-3 py-3 text-base border-gray-300 focus:outline-none focus:ring-orange focus:border-orange sm:text-sm rounded-md"
                  value={fieldValue}
                  onChange={(e) => {
                    onChange(credsData.field, e.currentTarget.value);
                  }}
                >
                  {credsData.options.selections.map((selection) => (
                    <option
                      data-testid={`${credsData.field}-${selection}`}
                      key={`${credsData.field}-${selection}`}
                      value={selection}
                    >
                      {selection}
                    </option>
                  ))}
                </select>
              </div>
            )}
          {credsData.field === "country" &&
            credsData.options.selections.length > 0 && (
              <div className="flex flex-col">
                <label className="pb-1 block text-sm whitespace-nowrap font-medium text-gray-700">
                  {credsData.prompt}
                </label>
                <Select
                  options={getCleanOptions(credsData.options.selections)}
                  value={getCleanOptions(credsData.options.selections).filter(
                    (option) => option.value === fieldValue
                  )}
                  onChange={(e: any) => {
                    onChange(credsData.field, e.value);
                  }}
                  isDisabled={!!currentFieldValues["server_hostname"]}
                  formatOptionLabel={(option: {
                    value: string;
                    label: string;
                  }) => (
                    <div className="flex flex-row items-center gap-2">
                      <img
                        src={`https://flagcdn.com/24x18/${option.value.toLowerCase()}.png`}
                        alt="flag"
                      />
                      {option.label}
                    </div>
                  )}
                  theme={(theme: any) => ({
                    ...theme,
                    borderRadius: 5,
                    colors: {
                      ...theme.colors,
                      primary: "#fda23f",
                      primary25: "#fda23f",
                    },
                  })}
                  styles={{
                    control: (base: any) => ({
                      ...base,
                      height: "48px",
                    }),
                    input: (base: any) => ({
                      ...base,
                      "input:focus": {
                        boxShadow: "none",
                      },
                    }),
                  }}
                />
              </div>
            )}
          {credsData.options.html_extra && (
            <div
              className="pb-0 mb-0 mt-2"
              dangerouslySetInnerHTML={{
                __html: credsData.options.html_extra,
              }}
            />
          )}
        </div>
      )}
    </>
  );
};

interface DropZoneProps {
  label: string;
  id: string;
  error: string;
  inputProps: any;
  acceptedTypes: [string];
  setDropzoneOff: () => void;
  selectedVPNProvider: VPNProvider;
}

const DropZone: React.FC<DropZoneProps> = ({
  label,
  id,
  error,
  inputProps,
  acceptedTypes,
  setDropzoneOff,
  selectedVPNProvider,
}) => {
  const { t } = useTranslation("securitySettings");
  const onDrop = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach((file: any) => {
        const reader = new FileReader();

        reader.onabort = () => console.log("file reading was aborted");
        reader.onerror = () => console.log("file reading has failed");
        reader.onload = () => {
          // Do whatever you want with the file contents
          const string = reader.result;
          console.log(string);
          inputProps.onChange(string);
          setDropzoneOff();
        };
        reader.readAsText(file);
      });
    },
    [inputProps, setDropzoneOff]
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxSize: 50000,
    maxFiles: 1,
    onDrop,
  });

  return (
    <div>
      <div {...getRootProps()} style={dzStyles.container}>
        <input {...getInputProps()} />
        <div style={dzStyles.innerContainer}>
          <IonIcon
            icon={documentTextOutline}
            style={isDragActive ? dzStyles.iconDrag : dzStyles.iconNormal}
            aria-hidden="true"
          />
          {selectedVPNProvider.provider === "wireguard" && (
            <p>{t("dragWireguard")}</p>
          )}
          {selectedVPNProvider.provider === "openvpn" && (
            <p>{t("dragOpenvpn")}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const dzStyles = {
  container: {
    cursor: "pointer",
    width: "100%",
    height: "100px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "2px dashed #737373",
    backgroundColor: "#d6d6d6",
    borderRadius: "3px",
    fontFamily: "Inter var",
    boxShadow: "0px 0px 2px #d6d6d6",
  },
  innerContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column" as "column",
    width: "65%",
    gap: "10px",
  },
  iconNormal: {
    transition: "0.3s",
    fontSize: "24px",
  },
  iconDrag: {
    transition: "0.3s",
    color: "#f97516",
    fontSize: "28px",
  },
};
